/**
 * ТЗ 0093 этап 2б — чистый слой runtime-настроек: «Роботы и расписания» и
 * «ИИ».
 *
 * Сервер отдаёт машинные значения: JSON-текст значения, `source`
 * (`env|db|stand`), `mode` (`ceiling|override`), `danger`, коды ошибок,
 * статусы журналов доменов, cron в UTC. Здесь — единственное место, которое
 * знает, что каждое из них ЗНАЧИТ на экране, чтобы строка ключа, таблица
 * расписаний и журнал изменений не разошлись между собой.
 *
 * Без React, Apollo и i18n: подписи возвращаются дескрипторами
 * (`{ key, values }` или `{ text }`, `ILabel`), компонент разрешает их своим
 * `t`. Таблицы ключей ниже обходит локаль-тест (`runtimeSettings.locale`):
 * код, добавленный без перевода, роняет CI, а не показывает сырой ключ.
 */

import { DateTime } from 'luxon';

import type {
  RuntimeSettingFieldsFragment,
  RuntimeSettingHistoryQuery,
  ScheduledRunFieldsFragment,
  ScheduledTasksListQuery,
} from 'core/graphql';
import type { ILabel } from 'core/utils/lmsSuspension';
import { formatWarsaw, WARSAW_TZ } from 'core/utils/zoomTime';

import { dofinMailHref, lmsAccessHref, smsSettingsHref } from './settingsLinks';
import type { SettingsSectionId } from './settingsSections';

export const RUNTIME_NS = 'settingsHub.runtime';
export const ROBOTS_NS = 'settingsHub.robots';
export const AI_NS = 'settingsHub.ai';

/** Дата и время в строках экрана — по Варшаве. */
export const RUNTIME_WHEN_FORMAT = 'dd.LL.yyyy HH:mm';

/** Раздел сервера, у которого свой раздел хаба («ИИ»). */
export const RUNTIME_AI_SECTION = 'ai';

/* ── Значения ───────────────────────────────────────────────────────── */

export type RuntimeType = 'bool' | 'int' | 'float' | 'str' | 'choice' | 'list';
export type RuntimeScalar = boolean | number | string | string[] | null;

/**
 * JSONString сервера → значение. На выходе graphene отдаёт JSON-текст
 * (`"false"`, `"14"`, `"\"claude-haiku-4-5\""`); `null` — значения нет.
 * Текст, который не разобрался, остаётся текстом (не роняет экран).
 */
export const parseRuntimeJson = (raw: unknown): unknown => {
  if (raw === null || raw === undefined) return null;
  if (typeof raw !== 'string') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
};

const asScalar = (value: unknown): RuntimeScalar => {
  if (value === null || value === undefined) return null;
  if (Array.isArray(value)) return value.map((item) => String(item));
  if (
    typeof value === 'boolean' ||
    typeof value === 'number' ||
    typeof value === 'string'
  ) {
    return value;
  }
  return JSON.stringify(value);
};

const isPolish = (language?: string | null): boolean =>
  (language || '').toLowerCase().startsWith('pl');

/** Подпись сервера на языке интерфейса; пустая — вторая. */
export const pickRuntimeLabel = (
  language: string | null | undefined,
  ru: string | null | undefined,
  pl: string | null | undefined,
): string => (isPolish(language) ? pl || ru : ru || pl) || '';

/* ── Ключ ───────────────────────────────────────────────────────────── */

export interface IRuntimeRun {
  beatKey: string;
  at: string | null;
  /**
   * `journal` — итог из журнала домена; `periodic_task` — только отправка
   * планировщиком; `skipped` — запуск пропустил гейт (выключатель выключен).
   */
  source: string;
  status: string | null;
  error: string | null;
  triggerSource: string | null;
}

export interface IRuntimeSetting {
  key: string;
  section: string;
  /** ТЗ 0095: смысловой раздел (`marketing`, `sales`…) — код сервера. */
  domain: string;
  type: string;
  mode: string;
  /**
   * Агенты ИИ (0104): env только разрешает, включают «Настройки». Без строки —
   * выключено, и сброс тоже выключает, поэтому «Сбросить» у них не нужен.
   */
  optIn: boolean;
  danger: string;
  label: string;
  help: string;
  value: RuntimeScalar;
  envValue: RuntimeScalar;
  dbValue: RuntimeScalar;
  hasDbValue: boolean;
  source: string;
  serverForbids: boolean;
  standBlocks: boolean;
  readOnly: boolean;
  choices: string[];
  updatedAt: string | null;
  updatedByName: string | null;
  beatKeys: string[];
  lastRun: IRuntimeRun | null;
}

export const toRuntimeRun = (
  raw: ScheduledRunFieldsFragment | null | undefined,
): IRuntimeRun | null =>
  raw
    ? {
        beatKey: raw.beatKey,
        at: raw.at ?? null,
        source: raw.source,
        status: raw.status ?? null,
        error: raw.error ?? null,
        triggerSource: raw.triggerSource ?? null,
      }
    : null;

export const toRuntimeSetting = (
  raw: RuntimeSettingFieldsFragment,
  language?: string | null,
): IRuntimeSetting => ({
  key: raw.key,
  section: raw.section,
  domain: raw.domain,
  type: raw.type,
  mode: raw.mode,
  optIn: Boolean(raw.optIn),
  danger: raw.danger || '',
  label: pickRuntimeLabel(language, raw.labelRu, raw.labelPl) || raw.key,
  help: pickRuntimeLabel(language, raw.helpRu, raw.helpPl),
  value: asScalar(parseRuntimeJson(raw.value)),
  envValue: asScalar(parseRuntimeJson(raw.envValue)),
  dbValue: asScalar(parseRuntimeJson(raw.dbValue)),
  hasDbValue: Boolean(raw.hasDbValue),
  source: raw.source,
  serverForbids: Boolean(raw.serverForbids),
  standBlocks: Boolean(raw.standBlocks),
  readOnly: Boolean(raw.readOnly),
  choices: raw.choices ?? [],
  updatedAt: raw.updatedAt ?? null,
  updatedByName: raw.updatedByName ?? null,
  beatKeys: raw.beatKeys ?? [],
  lastRun: toRuntimeRun(raw.lastRun),
});

/**
 * Подписи, которые фронт называет сам — ради одного термина во всей
 * программе: робот блокировки везде «Робот блокировки должников», а сервер
 * пока подписывает ключ «Робот приостановки LMS за неуплату». Когда подпись
 * сервера совпадёт, ключ отсюда убрать.
 */
export const RUNTIME_LABEL_OVERRIDES = ['LMS_BLOCK_ROBOT_ENABLED'] as const;

export const runtimeLabelOverrideKey = (key: string): string | null =>
  (RUNTIME_LABEL_OVERRIDES as readonly string[]).includes(key)
    ? `${RUNTIME_NS}.labels.${key}`
    : null;

export const settingsByKey = (
  settings: readonly IRuntimeSetting[],
): Map<string, IRuntimeSetting> =>
  new Map(settings.map((setting) => [setting.key, setting]));

/* ── Что можно сделать с ключом ─────────────────────────────────────── */

/**
 * Почему выключатель недоступен:
 * * `readOnly` — ключ показан, но в коде ещё не читается через настройки;
 * * `stand` — тестовая среда: ceiling (живые каналы, фискальное, платное)
 *   закрыт при любом значении, а внешний override (синхронизации, роботы с
 *   внешним эффектом) — «включить здесь нельзя, выключить можно»;
 * * `server` — env сервера = выключено: из ERP можно только ВЫКЛЮЧИТЬ.
 * `null` — менять можно.
 *
 * 🔴 Запреты проверяются РАНЬШЕ режима: у внешних override-ключей стенд
 * тоже отдаёт `standBlocks`/`serverForbids` (бэкенд 0093, `843e3cb7`), и
 * выход на `mode !== 'ceiling'` открывал их включение на стенде. Включённый
 * ключ выключить можно всегда — замок только на включение.
 */
export type RuntimeLock = 'readOnly' | 'stand' | 'server';

export const runtimeLock = (setting: IRuntimeSetting): RuntimeLock | null => {
  if (setting.readOnly) return 'readOnly';
  if (setting.value === true) return null;
  if (setting.standBlocks) return 'stand';
  if (setting.serverForbids) return 'server';
  return null;
};

export const RUNTIME_LOCK_KEYS: Readonly<Record<RuntimeLock, string>> = {
  readOnly: `${RUNTIME_NS}.lock.readOnly`,
  stand: `${RUNTIME_NS}.lock.stand`,
  server: `${RUNTIME_NS}.lock.server`,
};

/**
 * Что именно закрыла тестовая среда у ключа: ceiling — по опасности (живые
 * отправки, платные вызовы, фискальное), override — внешние действия
 * (синхронизации и роботы, которые пишут в чужие системы).
 */
export type StandCategory = 'live' | 'paid' | 'fiscal' | 'external';

export const standCategory = (
  setting: Pick<IRuntimeSetting, 'mode' | 'danger'>,
): StandCategory => {
  if (setting.mode !== 'ceiling') return 'external';
  if (setting.danger === 'paid' || setting.danger === 'fiscal') {
    return setting.danger;
  }
  return 'live';
};

/**
 * Пояснение называет то, что закрыто у ЭТОГО ключа: «живые каналы
 * выключены» под выключателем ИИ-функции или синхронизации читалось бы как
 * ошибка.
 */
export const RUNTIME_STAND_LOCK_KEYS: Readonly<
  Record<StandCategory, string>
> = {
  live: `${RUNTIME_NS}.lock.stand`,
  paid: `${RUNTIME_NS}.lock.standPaid`,
  fiscal: `${RUNTIME_NS}.lock.standFiscal`,
  external: `${RUNTIME_NS}.lock.standExternal`,
};

export const runtimeLockKey = (
  setting: Pick<IRuntimeSetting, 'mode' | 'danger'>,
  lock: RuntimeLock,
): string =>
  lock === 'stand'
    ? RUNTIME_STAND_LOCK_KEYS[standCategory(setting)]
    : RUNTIME_LOCK_KEYS[lock];

const STAND_ONCE_KEYS: Readonly<Record<StandCategory, string>> = {
  live: `${RUNTIME_NS}.lock.standOnce`,
  paid: `${RUNTIME_NS}.lock.standOncePaid`,
  fiscal: `${RUNTIME_NS}.lock.standOnceFiscal`,
  external: `${RUNTIME_NS}.lock.standOnceExternal`,
};

/** Порядок видов в сводной строке «тестовая среда». */
export const STAND_CATEGORIES: readonly StandCategory[] = [
  'live',
  'external',
  'fiscal',
  'paid',
];

export interface IRuntimeStandNote {
  key: string;
  /** Что закрыто — для сводной строки (`standOnceMixed`, `{{- what}}`). */
  what: StandCategory[];
}

/**
 * Одна строка «тестовая среда» над списком вместо строки в каждой строке
 * ключа (UX-ревью 0093: в «Каналах» стенда их было десять подряд). Текст
 * называет, что закрыто: живые отправки, платные вызовы, фискальное,
 * внешние действия — или перечисляет те виды, что есть в списке (а не все
 * сразу). Никто не закрыт стендом — `null`.
 */
export const runtimeStandNote = (
  settings: readonly IRuntimeSetting[],
): IRuntimeStandNote | null => {
  const present = new Set(
    settings
      .filter((setting) => runtimeLock(setting) === 'stand')
      .map(standCategory),
  );
  const what = STAND_CATEGORIES.filter((category) => present.has(category));
  if (!what.length) return null;
  if (what.length > 1)
    return { key: `${RUNTIME_NS}.lock.standOnceMixed`, what };
  return { key: STAND_ONCE_KEYS[what[0]], what };
};

export const runtimeStandNoteKey = (
  settings: readonly IRuntimeSetting[],
): string | null => runtimeStandNote(settings)?.key ?? null;

export const standWhatKey = (category: StandCategory): string =>
  `${RUNTIME_NS}.lock.standWhat.${category}`;

/** «Сбросить к значению сервера» — есть что сбрасывать и ключ не только для чтения. */
export const canResetSetting = (setting: IRuntimeSetting): boolean =>
  setting.hasDbValue && !setting.readOnly && !setting.optIn;

/** Для danger-ключа причина обязательна — и на запись, и на сброс. */
export const needsReason = (
  setting: Pick<IRuntimeSetting, 'danger'>,
): boolean => Boolean(setting.danger);

/* ── Бейджи ─────────────────────────────────────────────────────────── */

export type RuntimeTone =
  | 'ok'
  | 'warn'
  | 'danger'
  | 'neutral'
  | 'info'
  | 'gold';

export const RUNTIME_DANGERS = ['live', 'fiscal', 'paid'] as const;
export type RuntimeDanger = typeof RUNTIME_DANGERS[number];

export const RUNTIME_DANGER_TONES: Readonly<
  Record<RuntimeDanger, RuntimeTone>
> = {
  live: 'danger',
  fiscal: 'gold',
  paid: 'warn',
};

/** Бейдж и пояснение опасности; незнакомая опасность — общий бейдж. */
export const dangerBadge = (
  danger: string,
): { label: ILabel; hint: ILabel; tone: RuntimeTone } | null => {
  if (!danger) return null;
  const known = (RUNTIME_DANGERS as readonly string[]).includes(danger)
    ? (danger as RuntimeDanger)
    : null;
  if (!known) {
    return {
      label: { text: danger },
      hint: { key: `${RUNTIME_NS}.danger.otherHint` },
      tone: 'warn',
    };
  }
  return {
    label: { key: `${RUNTIME_NS}.danger.${known}` },
    hint: { key: `${RUNTIME_NS}.danger.${known}Hint` },
    tone: RUNTIME_DANGER_TONES[known],
  };
};

export const RUNTIME_SOURCES = ['env', 'db', 'stand'] as const;

const SOURCE_TONES: Readonly<Record<string, RuntimeTone>> = {
  env: 'neutral',
  db: 'info',
  stand: 'warn',
};

/**
 * Откуда значение: «значение сервера», «изменено в ERP — кто, когда»,
 * «стенд». Имя и время — в подсказке строки, в бейдже коротко.
 */
export const sourceBadge = (
  setting: Pick<IRuntimeSetting, 'source' | 'updatedByName' | 'updatedAt'>,
): { label: ILabel; detail: ILabel | null; tone: RuntimeTone } => {
  const tone = SOURCE_TONES[setting.source] ?? 'neutral';
  if (setting.source === 'db') {
    const when = setting.updatedAt
      ? formatWarsaw(setting.updatedAt, RUNTIME_WHEN_FORMAT)
      : '';
    let detail: ILabel | null = null;
    if (setting.updatedByName && when) {
      detail = {
        key: `${RUNTIME_NS}.source.dbByWhen`,
        values: { who: setting.updatedByName, when },
      };
    } else if (when) {
      detail = { key: `${RUNTIME_NS}.source.dbWhen`, values: { when } };
    }
    return { label: { key: `${RUNTIME_NS}.source.db` }, detail, tone };
  }
  if (setting.source === 'stand') {
    return { label: { key: `${RUNTIME_NS}.source.stand` }, detail: null, tone };
  }
  if (setting.source === 'env') {
    return { label: { key: `${RUNTIME_NS}.source.env` }, detail: null, tone };
  }
  return { label: { text: setting.source }, detail: null, tone };
};

export interface IRuntimeBadge {
  label: ILabel;
  tone: RuntimeTone;
}

/**
 * Бейджи источника в строке ключа. Над списком сказано «выключатели с
 * бейджем «Тестовая среда» отсюда не включить», значит бейдж обязан стоять у
 * КАЖДОЙ строки, которую стенд не даёт включить. Ceiling-ключам его даёт
 * сервер (`source: 'stand'`), а внешнему override-ключу (Kommo, дофин,
 * сверка Documenso, маркетинг) — нет: у него источник «по умолчанию», и
 * запертый переключатель стоял без объяснения.
 *
 * Значение по умолчанию на стенде ничего не решает — решает стенд, поэтому
 * «По умолчанию» он заменяет; «Изменено в ERP» остаётся рядом (кто и когда
 * выключил — правда, которую стенд не отменяет).
 */
export const runtimeSourceBadges = (
  setting: IRuntimeSetting,
): IRuntimeBadge[] => {
  const { label, tone } = sourceBadge(setting);
  const own: IRuntimeBadge = { label, tone };
  if (runtimeLock(setting) !== 'stand' || setting.source === 'stand') {
    return [own];
  }
  const stand: IRuntimeBadge = {
    label: { key: `${RUNTIME_NS}.source.stand` },
    tone: SOURCE_TONES.stand,
  };
  return setting.source === 'env' ? [stand] : [own, stand];
};

/* ── Варианты выбора: модели и глубина размышления ─────────────────── */

/**
 * Модели из белого списка сервера — имя и характеристика: Claude и (ТЗ 0104)
 * DeepSeek у агентов. Незнакомый id (сервер добавил модель, фронт ещё не
 * знает) показывается как есть.
 */
const MODEL_NAMES: Readonly<Record<string, string>> = {
  'claude-fable-5-1': 'Claude Fable 5.1',
  'claude-opus-5-5': 'Claude Opus 5.5',
  'claude-sonnet-5-5': 'Claude Sonnet 5.5',
  'claude-haiku-5-5': 'Claude Haiku 5.5',
  'claude-haiku-4-5': 'Claude Haiku 4.5',
  'claude-opus-4-6': 'Claude Opus 4.6',
  'claude-sonnet-4-6': 'Claude Sonnet 4.6',
  'claude-sonnet-4-5': 'Claude Sonnet 4.5',
  'deepseek-v4-pro': 'DeepSeek V4 Pro',
  'deepseek-flash': 'DeepSeek Flash',
};

export const AI_MODEL_TIERS = [
  'top',
  'precise',
  'balanced',
  'fast',
  'previous',
] as const;
export type AiModelTier = typeof AI_MODEL_TIERS[number];

const MODEL_TIER_OF: Readonly<Record<string, AiModelTier>> = {
  'claude-fable-5-1': 'top',
  'claude-opus-5-5': 'precise',
  'claude-sonnet-5-5': 'balanced',
  'claude-haiku-5-5': 'fast',
  // Прежнее поколение: в выборе остаётся, только если стоит в env сервера.
  'claude-opus-4-6': 'previous',
  'claude-sonnet-4-6': 'previous',
  'claude-haiku-4-5': 'previous',
  'claude-sonnet-4-5': 'previous',
  'deepseek-v4-pro': 'precise',
  'deepseek-flash': 'fast',
};

export const AI_EFFORT_LEVELS = [
  'low',
  'medium',
  'high',
  'xhigh',
  'max',
] as const;

const isModelChoice = (choice: string): boolean =>
  choice.startsWith('claude-') || choice.startsWith('deepseek-');

/**
 * Подпись варианта выбора. Модель — «Claude Sonnet 4.6 — баланс»
 * (`{{- name}}` — имя модели как есть), глубина — словами; прочее — как есть.
 */
export const runtimeChoiceLabel = (key: string, choice: string): ILabel => {
  if (isModelChoice(choice)) {
    const name = MODEL_NAMES[choice];
    const tier = MODEL_TIER_OF[choice];
    if (name && tier) {
      return { key: `${AI_NS}.models.${tier}`, values: { name } };
    }
    return { text: choice };
  }
  if (
    key.endsWith('_EFFORT') &&
    (AI_EFFORT_LEVELS as readonly string[]).includes(choice)
  ) {
    return { key: `${AI_NS}.effort.${choice}` };
  }
  return { text: choice };
};

/* ── Показ значения ─────────────────────────────────────────────────── */

/**
 * Значение словами — в строке ключа («значение сервера: …»), в журнале
 * изменений и в окне причины. `null` — «не задано».
 */
export const formatRuntimeValue = (
  setting: Pick<IRuntimeSetting, 'key' | 'type'> | null | undefined,
  value: unknown,
): ILabel => {
  if (value === null || value === undefined) {
    return { key: `${RUNTIME_NS}.value.none` };
  }
  if (typeof value === 'boolean') {
    return { key: `${RUNTIME_NS}.value.${value ? 'on' : 'off'}` };
  }
  if (Array.isArray(value)) {
    return value.length
      ? { text: value.map(String).join(', ') }
      : { key: `${RUNTIME_NS}.value.emptyList` };
  }
  if (typeof value === 'string') {
    if (!value.trim()) return { key: `${RUNTIME_NS}.value.empty` };
    if (setting && setting.type === 'choice') {
      return runtimeChoiceLabel(setting.key, value);
    }
    return { text: value };
  }
  if (typeof value === 'number') return { text: String(value) };
  return { text: JSON.stringify(value) };
};

/* ── Правка значения ────────────────────────────────────────────────── */

/** Черновик поля — строка; список — по одному значению на строку. */
export const runtimeValueToDraft = (
  type: string,
  value: RuntimeScalar,
): string => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.join('\n');
  if (type === 'list' && typeof value === 'string') return value;
  return String(value);
};

export type RuntimeDraftResult =
  | { ok: true; value: RuntimeScalar }
  | { ok: false; error: ILabel };

const INT_RE = /^-?\d+$/;
const FLOAT_RE = /^-?(\d+([.,]\d*)?|[.,]\d+)$/;

/**
 * Черновик → значение для `JSON.stringify`. Проверка здесь — только форма
 * (целое, число); пределы и допустимые варианты проверяет сервер
 * (`invalid_value`).
 */
export const runtimeDraftToValue = (
  type: string,
  draft: string,
): RuntimeDraftResult => {
  const text = draft.trim();
  switch (type) {
    case 'int':
      return INT_RE.test(text)
        ? { ok: true, value: parseInt(text, 10) }
        : { ok: false, error: { key: `${RUNTIME_NS}.validation.int` } };
    case 'float':
      return FLOAT_RE.test(text)
        ? { ok: true, value: Number(text.replace(',', '.')) }
        : { ok: false, error: { key: `${RUNTIME_NS}.validation.float` } };
    case 'list':
      return {
        ok: true,
        value: draft
          .split(/[\n,]/)
          .map((item) => item.trim())
          .filter(Boolean),
      };
    case 'choice':
      return text
        ? { ok: true, value: text }
        : { ok: false, error: { key: `${RUNTIME_NS}.validation.choice` } };
    default:
      return { ok: true, value: text };
  }
};

export const sameRuntimeValue = (a: unknown, b: unknown): boolean =>
  JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/* ── Второй выключатель ─────────────────────────────────────────────── */

/**
 * Три домена держат свой выключатель в своём разделе (ТЗ 0081, 0074, 0085):
 * канал SMS, робот блокировки должников, пауза рассылок дофина. Итог — оба
 * должны быть включены. Здесь их НЕ дублируем: строка ключа ведёт туда.
 */
export interface ISecondSwitch {
  section: SettingsSectionId;
  href: () => string;
}

export const SECOND_SWITCHES: Readonly<Record<string, ISecondSwitch>> = {
  SMS_ENABLED: { section: 'sms', href: () => smsSettingsHref('settings') },
  SMS_REMINDERS_ENABLED: {
    section: 'sms',
    href: () => smsSettingsHref('settings'),
  },
  LMS_BLOCK_ROBOT_ENABLED: { section: 'lmsAccess', href: lmsAccessHref },
  LMS_BLOCK_MAIL_ENABLED: { section: 'lmsAccess', href: lmsAccessHref },
  LMS_BLOCK_SLACK_ENABLED: { section: 'lmsAccess', href: lmsAccessHref },
  DOFIN_MAIL_ENABLED: { section: 'dofinMail', href: dofinMailHref },
};

export const secondSwitchFor = (key: string): ISecondSwitch | null =>
  SECOND_SWITCHES[key] ?? null;

/* ── Ошибки ─────────────────────────────────────────────────────────── */

export const RUNTIME_ERROR_CODES = [
  'unknown_key',
  'forbidden',
  'read_only',
  'invalid_value',
  'reason_required',
  'not_orphan',
  'not_found',
  'network',
] as const;

export interface IRuntimeError {
  field: string;
  code: string;
  message: string;
}

export interface IRuntimeActionResult {
  ok: boolean;
  /** `false` при успехе — значение уже такое, журнал не записан. */
  changed: boolean;
  errors: IRuntimeError[];
}

export const runtimeErrorLabel = (error: IRuntimeError): ILabel => {
  if ((RUNTIME_ERROR_CODES as readonly string[]).includes(error.code)) {
    return { key: `${RUNTIME_NS}.errors.${error.code}` };
  }
  return error.message
    ? { text: error.message }
    : { key: `${RUNTIME_NS}.errors.unknown` };
};

interface IPayload {
  success?: boolean | null;
  changed?: boolean | null;
  customErrors?: Array<{
    field?: string | null;
    code?: string | null;
    message?: string | null;
  } | null> | null;
}

export const toRuntimeActionResult = (
  payload: IPayload | null | undefined,
): IRuntimeActionResult => {
  const errors = (payload?.customErrors ?? [])
    .filter((error): error is NonNullable<typeof error> => Boolean(error))
    .map((error) => ({
      field: error.field ?? '',
      code: error.code ?? '',
      message: error.message ?? '',
    }));
  if (!payload) {
    errors.push({ field: '', code: 'unknown', message: '' });
  }
  const ok = Boolean(payload?.success) && errors.length === 0;
  return { ok, changed: ok && Boolean(payload?.changed), errors };
};

export const runtimeCrashResult = (caught: unknown): IRuntimeActionResult => ({
  ok: false,
  changed: false,
  errors: [
    {
      field: '',
      code: 'network',
      message: caught instanceof Error ? caught.message : '',
    },
  ],
});

/* ── Прогоны ────────────────────────────────────────────────────────── */

type RunGroup = 'success' | 'failed' | 'partial' | 'running' | 'skipped';

/** Статусы журналов доменов (Zoom, дофин, Kommo, маркетинг…) → группа. */
const RUN_STATUS_GROUPS: Readonly<Record<string, RunGroup>> = {
  success: 'success',
  succeeded: 'success',
  ok: 'success',
  done: 'success',
  completed: 'success',
  finished: 'success',
  failed: 'failed',
  failure: 'failed',
  error: 'failed',
  errored: 'failed',
  partial: 'partial',
  warning: 'partial',
  running: 'running',
  // Синк коммуникаций: упал и поставлен на повтор — ещё в работе.
  retry: 'running',
  started: 'running',
  in_progress: 'running',
  pending: 'running',
  queued: 'running',
  skipped: 'skipped',
};

export const RUNTIME_RUN_GROUPS: readonly RunGroup[] = [
  'success',
  'failed',
  'partial',
  'running',
  'skipped',
];

const RUN_TONES: Readonly<Record<RunGroup, RuntimeTone>> = {
  success: 'ok',
  failed: 'danger',
  partial: 'warn',
  running: 'info',
  skipped: 'neutral',
};

/**
 * Итог прогона словами. Строка только из `PeriodicTask` итога не знает —
 * «отправлено планировщиком», а не «успешно»: иначе упавшая задача
 * рапортовала бы об успехе.
 */
export const runStatus = (
  run: IRuntimeRun,
): { label: ILabel; tone: RuntimeTone } | null => {
  if (run.source === 'skipped') {
    return {
      label: {
        key: `${RUNTIME_NS}.run.skippedAt`,
        values: { when: run.at ? formatWarsaw(run.at, 'dd.LL HH:mm') : '—' },
      },
      tone: 'neutral',
    };
  }
  if (run.source === 'periodic_task') {
    return { label: { key: `${RUNTIME_NS}.run.sent` }, tone: 'neutral' };
  }
  const status = (run.status || '').toLowerCase();
  if (!status) return null;
  const group = RUN_STATUS_GROUPS[status];
  if (group) {
    return {
      label: { key: `${RUNTIME_NS}.run.status.${group}` },
      tone: RUN_TONES[group],
    };
  }
  return { label: { text: run.status || '' }, tone: 'neutral' };
};

export const RUNTIME_RUN_TRIGGERS = ['beat', 'erp', 'cli', 'manual'] as const;

export const runTrigger = (run: IRuntimeRun): ILabel | null => {
  const trigger = (run.triggerSource || '').toLowerCase();
  if (!trigger) return null;
  return (RUNTIME_RUN_TRIGGERS as readonly string[]).includes(trigger)
    ? { key: `${RUNTIME_NS}.run.trigger.${trigger}` }
    : { text: run.triggerSource || '' };
};

export const formatRuntimeWhen = (at: string | null | undefined): string =>
  at ? formatWarsaw(at, RUNTIME_WHEN_FORMAT) : '—';

/* ── Расписание: cron UTC → словами по Варшаве ──────────────────────── */

export type ScheduleDescription =
  | { kind: 'everyMinute' }
  | { kind: 'everyMinutes'; n: number; offset: number }
  | { kind: 'hourly'; minute: number }
  | { kind: 'everyHours'; n: number }
  | { kind: 'everyDays'; n: number }
  | { kind: 'everySeconds'; n: number }
  | { kind: 'daily'; times: string[] }
  | { kind: 'hourStep'; n: number; from: string; to: string }
  | { kind: 'weekly'; weekday: number; times: string[] }
  | { kind: 'raw'; schedule: string };

const pad2 = (value: number): string => String(value).padStart(2, '0');

/** Поле cron → отсортированные значения; незнакомая форма — `null`. */
const expandCronField = (
  field: string,
  min: number,
  max: number,
): number[] | null => {
  const out = new Set<number>();
  const parts = field.split(',');
  for (let i = 0; i < parts.length; i += 1) {
    const match = /^(\*|\d+(?:-\d+)?)(?:\/(\d+))?$/.exec(parts[i]);
    if (!match) return null;
    const [, range, stepText] = match;
    const step = stepText ? parseInt(stepText, 10) : 1;
    if (step < 1) return null;
    let from = min;
    let to = max;
    if (range !== '*') {
      const [a, b] = range.split('-').map((part) => parseInt(part, 10));
      from = a;
      // `5/15` — от 5 до конца с шагом; `5` — одно значение.
      to = b !== undefined ? b : stepText ? max : a;
    }
    if (from < min || to > max || from > to) return null;
    for (let value = from; value <= to; value += step) out.add(value);
  }
  return Array.from(out).sort((a, b) => a - b);
};

/** Шаг равномерной последовательности (≥2 значений); иначе `null`. */
const evenStep = (values: number[]): number | null => {
  if (values.length < 2) return null;
  const step = values[1] - values[0];
  for (let i = 2; i < values.length; i += 1) {
    if (values[i] - values[i - 1] !== step) return null;
  }
  return step;
};

interface IWarsawTime {
  text: string;
  minutes: number;
  /** Сдвиг даты от UTC: 1 — уже следующий день по Варшаве. */
  dayShift: number;
}

const toWarsaw = (
  reference: DateTime,
  hour: number,
  minute: number,
): IWarsawTime => {
  const utc = DateTime.utc(
    reference.year,
    reference.month,
    reference.day,
    hour,
    minute,
  );
  const local = utc.setZone(WARSAW_TZ);
  // Варшава всегда впереди UTC (+1 / +2): другая дата — это следующий день.
  const dayShift = local.toISODate() === utc.toISODate() ? 0 : 1;
  return {
    text: `${pad2(local.hour)}:${pad2(local.minute)}`,
    minutes: local.hour * 60 + local.minute,
    dayShift,
  };
};

const MAX_LISTED_TIMES = 4;

/**
 * Расписание записи → описание. Сервер отдаёт crontab пятью полями в UTC
 * («30 5 * * 3») или интервал («every 300 s», у беспризорных строк —
 * «every 5 minutes»). Часы переводятся в Варшаву на дату `reference`
 * (сегодня): летом и зимой одна и та же запись идёт в разное местное время.
 * Незнакомая форма — `raw`, экран покажет cron как есть с пометкой UTC.
 */
export const describeSchedule = (
  schedule: string,
  reference: DateTime = DateTime.utc(),
): ScheduleDescription => {
  const text = (schedule || '').trim();
  const raw: ScheduleDescription = { kind: 'raw', schedule: text };

  const interval = /^every (\d+) ?(s|seconds?|minutes?|hours?|days?)$/i.exec(
    text,
  );
  if (interval) {
    const n = parseInt(interval[1], 10);
    const unit = interval[2].toLowerCase();
    if (!n) return raw;
    if (unit === 's' || unit.startsWith('second')) {
      return n % 60 === 0
        ? { kind: 'everyMinutes', n: n / 60, offset: 0 }
        : { kind: 'everySeconds', n };
    }
    if (unit.startsWith('minute'))
      return { kind: 'everyMinutes', n, offset: 0 };
    if (unit.startsWith('hour')) return { kind: 'everyHours', n };
    return { kind: 'everyDays', n };
  }

  const parts = text.split(/\s+/);
  if (parts.length !== 5) return raw;
  const [minuteField, hourField, domField, monthField, dowField] = parts;
  if (domField !== '*' || monthField !== '*') return raw;
  const minutes = expandCronField(minuteField, 0, 59);
  const hours = expandCronField(hourField, 0, 23);
  if (!minutes || !hours) return raw;
  let weekdays: number[] | null = null;
  if (dowField !== '*') {
    const days = expandCronField(dowField, 0, 7);
    if (!days) return raw;
    weekdays = Array.from(new Set(days.map((day) => day % 7)));
  }

  if (hourField === '*') {
    if (weekdays) return raw;
    if (minuteField === '*') return { kind: 'everyMinute' };
    if (minutes.length === 1) return { kind: 'hourly', minute: minutes[0] };
    const step = evenStep(minutes);
    if (step && 60 % step === 0 && minutes[0] < step) {
      return { kind: 'everyMinutes', n: step, offset: minutes[0] };
    }
    return raw;
  }

  const times: IWarsawTime[] = [];
  hours.forEach((hour) =>
    minutes.forEach((minute) => times.push(toWarsaw(reference, hour, minute))),
  );

  if (weekdays) {
    if (weekdays.length !== 1 || times.length > MAX_LISTED_TIMES) return raw;
    const shifts = new Set(times.map((time) => time.dayShift));
    if (shifts.size !== 1) return raw;
    const [shift] = Array.from(shifts);
    return {
      kind: 'weekly',
      weekday: (weekdays[0] + shift + 7) % 7,
      times: times.map((time) => time.text),
    };
  }

  if (times.length <= MAX_LISTED_TIMES) {
    return {
      kind: 'daily',
      times: [...times]
        .sort((a, b) => a.minutes - b.minutes)
        .map((time) => time.text),
    };
  }

  const hourStep = evenStep(hours);
  if (minutes.length === 1 && hourStep) {
    return {
      kind: 'hourStep',
      n: hourStep,
      from: times[0].text,
      to: times[times.length - 1].text,
    };
  }
  return raw;
};

export const CRON_WEEKDAYS = [0, 1, 2, 3, 4, 5, 6] as const;

/** Описание расписания словами. */
export const scheduleLabel = (description: ScheduleDescription): ILabel => {
  const CRON = `${ROBOTS_NS}.cron`;
  switch (description.kind) {
    case 'everyMinute':
      return { key: `${CRON}.everyMinute` };
    case 'everyMinutes':
      return description.offset
        ? {
            key: `${CRON}.everyMinutesFrom`,
            values: {
              n: description.n,
              minute: pad2(description.offset),
            },
          }
        : { key: `${CRON}.everyMinutes`, values: { n: description.n } };
    case 'hourly':
      return {
        key: `${CRON}.hourly`,
        values: { minute: pad2(description.minute) },
      };
    case 'everyHours':
      return { key: `${CRON}.everyHours`, values: { n: description.n } };
    case 'everyDays':
      return description.n === 1
        ? { key: `${CRON}.everyDay` }
        : { key: `${CRON}.everyDays`, values: { n: description.n } };
    case 'everySeconds':
      return { key: `${CRON}.everySeconds`, values: { n: description.n } };
    case 'daily':
      return {
        key: `${CRON}.daily`,
        values: { times: description.times.join(', ') },
      };
    case 'hourStep':
      return {
        key: `${CRON}.hourStep`,
        values: {
          n: description.n,
          from: description.from,
          to: description.to,
        },
      };
    case 'weekly':
      return {
        key: `${CRON}.weekly.${description.weekday}`,
        values: { times: description.times.join(', ') },
      };
    default:
      return { text: description.schedule };
  }
};

/* ── Расписания задач ───────────────────────────────────────────────── */

type RawEntry = ScheduledTasksListQuery['scheduledTasks']['entries'][number];
type RawOrphan = ScheduledTasksListQuery['scheduledTasks']['orphans'][number];

export interface IScheduledKeyState {
  key: string;
  on: boolean;
  source: string;
}

export interface IScheduledEntry {
  name: string;
  task: string;
  /** ТЗ 0095: смысловой раздел — по ключу гейта; служебные — `system`. */
  domain: string;
  schedule: string;
  keys: string[];
  offKeys: string[];
  keyStates: IScheduledKeyState[];
  effectiveOn: boolean;
  alwaysOn: boolean;
  /** `false` — строка выключена в админке; `null` — строки ещё нет. */
  periodicTaskEnabled: boolean | null;
  lastRunAt: string | null;
  totalRunCount: number | null;
  lastJournalRun: IRuntimeRun | null;
  /**
   * Когда гейт в последний раз пропустил задачу (выключатель выключен);
   * `null` — пропусков не было или кэш сервера их не помнит (месяц).
   */
  lastSkippedAt: string | null;
}

export interface IOrphanTask {
  name: string;
  task: string;
  enabled: boolean;
  schedule: string;
  lastRunAt: string | null;
  totalRunCount: number;
}

export const toScheduledEntry = (raw: RawEntry): IScheduledEntry => ({
  name: raw.name,
  task: raw.task,
  domain: raw.domain,
  schedule: raw.schedule,
  keys: raw.keys ?? [],
  offKeys: raw.offKeys ?? [],
  keyStates: (raw.keyStates ?? []).map((state) => ({
    key: state.key,
    on: Boolean(state.on),
    source: state.source,
  })),
  effectiveOn: Boolean(raw.effectiveOn),
  alwaysOn: Boolean(raw.alwaysOn),
  periodicTaskEnabled:
    raw.periodicTaskEnabled === null || raw.periodicTaskEnabled === undefined
      ? null
      : Boolean(raw.periodicTaskEnabled),
  lastRunAt: raw.lastRunAt ?? null,
  totalRunCount: raw.totalRunCount ?? null,
  lastJournalRun: toRuntimeRun(raw.lastJournalRun),
  lastSkippedAt: raw.lastSkippedAt ?? null,
});

export const toOrphanTask = (raw: RawOrphan): IOrphanTask => ({
  name: raw.name,
  task: raw.task,
  enabled: Boolean(raw.enabled),
  schedule: raw.schedule,
  lastRunAt: raw.lastRunAt ?? null,
  totalRunCount: raw.totalRunCount ?? 0,
});

/**
 * Ключ, по которому запись названа словами: из её ключей — самый частный
 * (гейтит меньше всего записей). У `outreach.run_reminders` ключи
 * `SMS_ENABLED` (две записи) и `SMS_REMINDERS_ENABLED` (одна) — запись
 * называется «напоминаниями», а не «каналом SMS».
 */
export const entryTitleSetting = (
  entry: Pick<IScheduledEntry, 'keys'>,
  byKey: ReadonlyMap<string, IRuntimeSetting>,
): IRuntimeSetting | null => {
  const known = entry.keys
    .map((key) => byKey.get(key))
    .filter((setting): setting is IRuntimeSetting => Boolean(setting));
  if (!known.length) return null;
  return known.reduce((best, setting) =>
    setting.beatKeys.length < best.beatKeys.length ? setting : best,
  );
};

const timeOf = (iso: string | null | undefined): number =>
  iso ? Date.parse(iso) : Number.NaN;

/**
 * Последний запуск записи. Пропуск гейтом (`lastSkippedAt`) — если он не
 * раньше отправки планировщиком и новее журнала домена: тогда последний
 * запуск beat'а был пропуском, и «отправлено планировщиком» врало бы. Иначе
 * — журнал домена, иначе — отправка планировщиком.
 */
export const entryLastRun = (entry: IScheduledEntry): IRuntimeRun | null => {
  const skipped = timeOf(entry.lastSkippedAt);
  if (!Number.isNaN(skipped)) {
    const sent = timeOf(entry.lastRunAt);
    const journal = timeOf(entry.lastJournalRun?.at);
    if (
      (Number.isNaN(sent) || skipped >= sent) &&
      (Number.isNaN(journal) || skipped > journal)
    ) {
      return {
        beatKey: entry.name,
        at: entry.lastSkippedAt,
        source: 'skipped',
        status: null,
        error: null,
        triggerSource: null,
      };
    }
  }
  if (entry.lastJournalRun) return entry.lastJournalRun;
  if (!entry.lastRunAt) return null;
  return {
    beatKey: entry.name,
    at: entry.lastRunAt,
    source: 'periodic_task',
    status: null,
    error: null,
    triggerSource: null,
  };
};

/* ── Расписания: домены, названия служебных записей, поиск ─────────── */

/**
 * Домены таблицы расписаний — в порядке показа. Пятьдесят записей одним
 * списком не читались (UX-ревью 0093): записи группируются по приложению, из
 * которого они (`zoom_recordings.*` → Zoom). Незнакомое приложение — «Прочее».
 */
export const SCHEDULE_DOMAINS = [
  'zoom',
  'dofin',
  'marketing',
  'kommo',
  'sms',
  'notifications',
  'students',
  'communications',
  'career',
  'documents',
  'finance',
  'system',
  'other',
] as const;
export type ScheduleDomain = typeof SCHEDULE_DOMAINS[number];

const SCHEDULE_DOMAIN_OF_APP: Readonly<Record<string, ScheduleDomain>> = {
  zoom_recordings: 'zoom',
  dofinansowanie: 'dofin',
  marketing: 'marketing',
  crm: 'kommo',
  outreach: 'sms',
  shortlinks: 'sms',
  notifications: 'notifications',
  students: 'students',
  communications: 'communications',
  career: 'career',
  esign: 'documents',
  pandadoc: 'documents',
  fakturownia: 'finance',
  mcp_access: 'system',
  tasks: 'system',
};

export const scheduleDomain = (name: string): ScheduleDomain =>
  SCHEDULE_DOMAIN_OF_APP[name.split('.')[0]] ?? 'other';

export interface IScheduleGroup {
  domain: ScheduleDomain;
  entries: IScheduledEntry[];
}

export const groupScheduledEntries = (
  entries: readonly IScheduledEntry[],
): IScheduleGroup[] =>
  SCHEDULE_DOMAINS.map((domain) => ({
    domain,
    entries: entries.filter((entry) => scheduleDomain(entry.name) === domain),
  })).filter((group) => group.entries.length > 0);

/**
 * Человеческие названия служебных записей без выключателя (у записей с
 * выключателем название — подпись самого частного ключа). Код записи
 * остаётся второй строкой.
 */
export const SCHEDULE_ENTRY_NAMES: Readonly<Record<string, string>> = {
  'tasks.create_db_dump': 'dbDump',
  'outreach.prune': 'smsPrune',
  'outreach.check_provider': 'smsProviderCheck',
  'outreach.revoke_closed_links': 'smsRevokeLinks',
  'shortlinks.prune': 'shortlinksPrune',
  'mcp_access.clear_expired_oauth_tokens': 'mcpTokens',
  'career.recompute_skill_demand': 'careerSkillDemand',
  'career.reconcile_promo_codes': 'careerPromoCodes',
};

export const scheduleEntryNameKey = (name: string): string | null => {
  const id = SCHEDULE_ENTRY_NAMES[name];
  return id ? `${ROBOTS_NS}.entryNames.${id}` : null;
};

/** Поиск по названию, коду записи, задаче и именам выключателей. */
export const matchesScheduleSearch = (
  entry: Pick<IScheduledEntry, 'name' | 'task' | 'keys'>,
  title: string,
  query: string,
): boolean => {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return [title, entry.name, entry.task, ...entry.keys].some((text) =>
    text.toLowerCase().includes(needle),
  );
};

/**
 * ТЗ 0095 — поиск ключа в хабе: код, подпись и пояснение. Каждое слово
 * запроса — подстрока (как у карточек хаба).
 */
export const matchesRuntimeSearch = (
  setting: Pick<IRuntimeSetting, 'key' | 'label' | 'help'>,
  query: string,
): boolean => {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return true;
  const haystack = [setting.key, setting.label, setting.help]
    .join('\n')
    .toLowerCase();
  return terms.every((term) => haystack.includes(term));
};

/* ── Журнал изменений ───────────────────────────────────────────────── */

/** Ключ журнала о выключении беспризорной строки планировщика. */
export const PERIODIC_TASK_AUDIT_KEY = 'periodic_task';

type RawChange = RuntimeSettingHistoryQuery['runtimeSettingChanges'][number];

export interface IRuntimeChange {
  id: string;
  key: string;
  old: unknown;
  new: unknown;
  reason: string;
  userName: string | null;
  createdAt: string;
}

export const toRuntimeChange = (raw: RawChange): IRuntimeChange => ({
  id: String(raw.id),
  key: raw.key,
  old: parseRuntimeJson(raw.old),
  new: parseRuntimeJson(raw.new),
  reason: raw.reason || '',
  userName: raw.userName ?? null,
  createdAt: raw.createdAt,
});

const auditRow = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

/** «Что изменили»: подпись ключа; выключенная строка планировщика — словами. */
export const changeTitle = (
  change: IRuntimeChange,
  byKey: ReadonlyMap<string, IRuntimeSetting>,
): ILabel => {
  if (change.key === PERIODIC_TASK_AUDIT_KEY) {
    const row = auditRow(change.new) ?? auditRow(change.old);
    return {
      key: `${ROBOTS_NS}.changes.periodicTask`,
      values: { name: String(row?.name ?? '') },
    };
  }
  const setting = byKey.get(change.key);
  return { text: setting ? setting.label : change.key };
};

/**
 * «Было» / «стало». `null` у ключа реестра — значения в ERP нет, действует
 * сервер (сброс или первая запись). У строки планировщика — вкл/выкл.
 */
export const changeValue = (
  change: IRuntimeChange,
  side: 'old' | 'new',
  byKey: ReadonlyMap<string, IRuntimeSetting>,
): ILabel => {
  const value = change[side];
  if (change.key === PERIODIC_TASK_AUDIT_KEY) {
    const row = auditRow(value);
    if (!row || typeof row.enabled !== 'boolean') {
      return { key: `${RUNTIME_NS}.value.none` };
    }
    return { key: `${RUNTIME_NS}.value.${row.enabled ? 'on' : 'off'}` };
  }
  if (value === null || value === undefined) {
    return { key: `${RUNTIME_NS}.value.server` };
  }
  return formatRuntimeValue(byKey.get(change.key) ?? null, value);
};

/* ── ИИ: ключи по функциям ──────────────────────────────────────────── */

/**
 * Раздел «ИИ» показывает ключи не списком, а по функциям: «что за функция →
 * включена ли → какой моделью». Ключ раздела `ai`, которого нет ни в одной
 * функции (сервер добавил новый), попадает в «Прочее» — не теряется.
 */
export const AI_FUNCTIONS = [
  { id: 'homework', keys: ['HOMEWORK_AI_ENABLED', 'AI_MODEL_HOMEWORK'] },
  { id: 'teacher', keys: ['TEACHER_AI_ENABLED', 'AI_MODEL_TEACHER'] },
  {
    id: 'marketing',
    keys: [
      'MARKETING_DIGEST_ENABLED',
      'MARKETING_DIGEST_MODEL',
      'MARKETING_DIGEST_EFFORT',
    ],
  },
  { id: 'transcripts', keys: ['AI_MODEL_TRANSCRIPTS'] },
  // Перепроверка F33: ключи ТЗ 0098 жили в «Прочем».
  {
    id: 'surveys',
    keys: [
      'SURVEY_AI_ENRICH_ENABLED',
      'AI_MODEL_SURVEYS',
      'SURVEY_DIGEST_ENABLED',
      'SURVEY_DIGEST_MODEL',
      'SURVEY_DIGEST_EFFORT',
    ],
  },
  // ТЗ 0104, §3.9 — ИИ-агент ERP (LangGraph + DeepSeek). Порядок ключей в
  // функции: выключатель → модель → бюджет → тонкая настройка. Ключ, которого
  // сервер (ещё) не отдаёт, просто не рисуется: пустая функция выпадает.
  {
    id: 'agents',
    keys: [
      'AI_AGENTS_ENABLED',
      'AI_MONTHLY_BUDGET_USD',
      'AI_KB_PREFIX_MAX_TOKENS',
    ],
  },
  {
    id: 'slackWatch',
    keys: [
      'AI_SLACK_WATCH_ENABLED',
      'AI_MODEL_SLACK_WATCH',
      'AI_BUDGET_SLACK_WATCH_USD',
      'AI_SLACK_WATCH_DEBOUNCE_SECONDS',
      'AI_SLACK_TASK_KINDS',
    ],
  },
  {
    id: 'replyDraft',
    keys: [
      'AI_REPLY_DRAFT_ENABLED',
      'AI_REPLY_AUTO_ENABLED',
      'AI_MODEL_REPLY_DRAFT',
      'AI_BUDGET_REPLY_DRAFT_USD',
      'AI_REPLY_DEBOUNCE_SECONDS',
    ],
  },
  {
    id: 'portrait',
    keys: [
      'AI_PORTRAIT_ENABLED',
      'AI_PORTRAIT_NIGHTLY_ENABLED',
      'AI_MODEL_PORTRAIT',
      'AI_BUDGET_PORTRAIT_USD',
      'AI_PORTRAIT_NIGHTLY_LIMIT',
    ],
  },
  {
    id: 'lessonAnalysis',
    keys: [
      'AI_LESSON_ANALYSIS_ENABLED',
      'AI_LESSON_AUTO_ENABLED',
      'AI_MODEL_LESSON_EXTRACT',
      'AI_MODEL_LESSON_SCORE',
      'AI_BUDGET_LESSON_ANALYSIS_USD',
      'AI_LESSON_NIGHTLY_LIMIT',
    ],
  },
  {
    id: 'chat',
    keys: ['AI_CHAT_ENABLED', 'AI_MODEL_CHAT', 'AI_BUDGET_CHAT_USD'],
  },
] as const;

export type AiFunctionId = typeof AI_FUNCTIONS[number]['id'] | 'other';

export interface IAiFunctionGroup {
  id: AiFunctionId;
  settings: IRuntimeSetting[];
}

export const groupAiSettings = (
  settings: readonly IRuntimeSetting[],
): IAiFunctionGroup[] => {
  const byKey = settingsByKey(settings);
  const used = new Set<string>();
  const groups: IAiFunctionGroup[] = [];
  AI_FUNCTIONS.forEach((fn) => {
    const found = (fn.keys as readonly string[])
      .map((key) => byKey.get(key))
      .filter((setting): setting is IRuntimeSetting => Boolean(setting));
    found.forEach((setting) => used.add(setting.key));
    if (found.length) groups.push({ id: fn.id, settings: found });
  });
  const rest = settings.filter((setting) => !used.has(setting.key));
  if (rest.length) groups.push({ id: 'other', settings: rest });
  return groups;
};

/**
 * Разделы сервера, которые фронт знает в лицо: у их вкладок короткая подпись
 * («Роботы», «Каналы») и пояснение над списком. Полная подпись сервера
 * («Роботы и расписания», «Интеграции и синхронизации») остаётся подсказкой
 * вкладки и подписью списка: семь длинных вкладок не помещались в строку на
 * 1100 px, а «Роботы и расписания» рядом с «Расписаниями» повторяли название
 * раздела. Раздел, которого фронт не знает, подписан как у сервера.
 */
export const ROBOTS_KNOWN_SECTIONS = [
  'robots',
  'integrations',
  'channels',
  'mail',
  'thresholds',
] as const;

const isKnownSection = (section: string): boolean =>
  (ROBOTS_KNOWN_SECTIONS as readonly string[]).includes(section);

export const robotsSectionHintKey = (section: string): string | null =>
  isKnownSection(section) ? `${ROBOTS_NS}.sectionHints.${section}` : null;

export const robotsTabLabelKey = (section: string): string | null =>
  isKnownSection(section) ? `${ROBOTS_NS}.tabs.${section}` : null;
